import type { ComponentPropsWithoutRef, ReactNode } from "react";
import {
  type Control,
  Controller,
  type ControllerFieldState,
  type ControllerRenderProps,
  type FieldValues,
  type Path,
} from "react-hook-form";

import { Field, FieldError, FieldLabel } from "./field";

export const InputWrapper = ({
  label,
  htmlFor,
  required = false,
  errors,
  children,
}: {
  label?: ReactNode;
  htmlFor?: string;
  required?: boolean;
  errors?: ComponentPropsWithoutRef<typeof FieldError>["errors"];
  children: ReactNode;
}) => {
  return (
    <Field data-invalid={!!errors}>
      {label && (
        <FieldLabel htmlFor={htmlFor}>
          {label} {required && <span className="text-red">*</span>}
        </FieldLabel>
      )}
      {children}
      {!!errors && errors.length > 0 && <FieldError errors={errors} />}
    </Field>
  );
};

interface IControlledWrapperProps<
  T extends FieldValues,
  K extends Path<T> = Path<T>,
> {
  label?: ReactNode;
  // Defaults to `name`, matching the `id={field.name}` convention on controls.
  htmlFor?: string;
  required?: boolean;
  errors?: ComponentPropsWithoutRef<typeof FieldError>["errors"];
  control: Control<T>;
  name: K;
  render: (props: {
    field: ControllerRenderProps<T, K>;
    fieldState: ControllerFieldState;
  }) => ReactNode;
}
export const ControlledWrapper = <
  T extends FieldValues,
  K extends Path<T> = Path<T>,
>({
  control,
  name,
  required = false,
  render,
  errors,
  label,
  htmlFor,
}: IControlledWrapperProps<T, K>) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <InputWrapper
          label={label}
          htmlFor={htmlFor ?? name}
          required={required}
          errors={
            fieldState.invalid
              ? [fieldState.error, ...(errors ?? [])]
              : undefined
          }
        >
          {render({ field, fieldState })}
        </InputWrapper>
      )}
    />
  );
};
